import React, { useState, useEffect, useRef } from 'react'
import { AutoComplete } from 'antd'
import {
  getStateNameOptions,
  getStateCodeFromName,
  getStateNameFromCode,
  isValidStateName,
  isValidStateCode,
} from '@helpers/stateValidation'
import './StateAutoComplete.scss'

/**
 * StateAutoComplete component that displays state names but stores state codes
 *
 * This component provides a better UX by showing full state names (e.g., "Alabama")
 * while maintaining backend compatibility by storing state codes (e.g., "AL")
 *
 * Supports keyboard navigation:
 * - Up/Down arrows to navigate through filtered options
 * - Enter to select the highlighted option
 * - Escape to close dropdown
 *
 * @param {string} value - The current state code value from the form
 * @param {function} onChange - Callback when state selection changes (receives state code)
 * @param {string} placeholder - Placeholder text
 * @param {string} size - Size of the input ('large', 'middle', 'small')
 * @param {object} ...props - Other props to pass to AutoComplete
 */
const StateAutoComplete = ({ value, onChange, placeholder = 'Select state', size = 'large', ...props }) => {
  // Display value (state name) separate from form value (state code)
  const [displayValue, setDisplayValue] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [isOpen, setIsOpen] = useState(false)
  const stateOptions = getStateNameOptions()
  const autoCompleteRef = useRef(null)

  // Convert state code to state name for display when value changes
  useEffect(() => {
    if (value && isValidStateCode(value)) {
      setDisplayValue(getStateNameFromCode(value))
    } else if (value && isValidStateName(value)) {
      // If form somehow has a state name, display it as-is
      setDisplayValue(value)
    } else if (!value) {
      setDisplayValue('')
    }
    // For invalid values, keep the current display value as-is
  }, [value])

  // Reset highlighted index when options change
  useEffect(() => {
    setHighlightedIndex(-1)
  }, [displayValue])

  const handleChange = (selectedValue) => {
    // Update display value
    setDisplayValue(selectedValue)
    setIsOpen(!!selectedValue)

    // Always call onChange so form validation can work properly
    if (onChange) {
      if (!selectedValue) {
        // Handle clearing the field
        onChange('')
        setIsOpen(false)
      } else if (isValidStateName(selectedValue)) {
        // If it's a valid state name, convert to state code for backend compatibility
        const stateCode = getStateCodeFromName(selectedValue)
        onChange(stateCode)
        setIsOpen(false)
      } else {
        // For invalid values, pass the display value so form validation can catch it
        // The validator will check if it's a valid state code or name
        onChange(selectedValue)
      }
    }
  }

  const handleSelect = (selectedValue) => {
    // When user selects from dropdown, convert to state code
    const stateCode = getStateCodeFromName(selectedValue)
    setDisplayValue(selectedValue)
    setIsOpen(false)
    setHighlightedIndex(-1)
    if (onChange) {
      onChange(stateCode)
    }
  }

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    const filteredOptions = getFilteredOptions()

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setIsOpen(true)
        setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        setIsOpen(true)
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1))
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          const selectedOption = filteredOptions[highlightedIndex]
          handleSelect(selectedOption.value)
        }
        break
      case 'Escape':
        setIsOpen(false)
        setHighlightedIndex(-1)
        if (autoCompleteRef.current) {
          autoCompleteRef.current.blur()
        }
        break
      default:
        setIsOpen(true)
        break
    }
  }

  // Filter options based on user input (allow searching by state name or code)
  const getFilteredOptions = () => {
    const searchValue = displayValue.toLowerCase()
    return stateOptions.filter((option) => {
      return option.value.toLowerCase().includes(searchValue) || option.code.toLowerCase().includes(searchValue)
    })
  }

  const filteredOptions = getFilteredOptions()

  // Add highlighting to options
  const highlightedOptions = filteredOptions.map((option, index) => ({
    ...option,
    className: index === highlightedIndex ? 'highlighted-option' : undefined,
  }))

  return (
    <AutoComplete
      ref={autoCompleteRef}
      className="state-autocomplete"
      value={displayValue}
      onChange={handleChange}
      onSelect={handleSelect}
      onKeyDown={handleKeyDown}
      onFocus={() => setIsOpen(true)}
      onBlur={() => {
        // Small delay to allow selection to complete
        setTimeout(() => {
          setIsOpen(false)
          setHighlightedIndex(-1)
        }, 150)
      }}
      placeholder={placeholder}
      size={size}
      open={isOpen && filteredOptions.length > 0}
      showSearch
      optionFilterProp="value"
      options={highlightedOptions}
      filterOption={false} // We handle filtering manually
      {...props}
    />
  )
}

export default StateAutoComplete
